import { useEffect, useMemo, useState } from "react";
import { useTranslate } from "ra-core";

import type { WhatsAppConversationState } from "../types";
import {
  barFraction,
  type ClockTone,
  formatRemaining,
  FREE_MESSAGES_WINDOW_MS,
  FREE_TEXT_WINDOW_MS,
  freeMessagesTone,
  freeTextTone,
  remainingMs,
  serverOffsetMs,
  TONE_COLORS,
} from "./whatsappClockView";

const TICK_MS = 30_000;

export type WhatsAppClocks = {
  /** Remaining time of the 24 h clock; `null` when the conversation has no inbound message yet. */
  freeText: number | null;
  /** Remaining time of the 7-day clock; `null` unless the lead came from a WhatsApp button (ctwa). */
  freeMessages: number | null;
};

/**
 * Renders the server deadlines as a live countdown: the remaining time is the deadline minus the
 * SERVER clock (server `now` at read time, advanced by the browser's elapsed time), re-evaluated
 * every 30 s without refetching. No clock rule is computed here.
 */
export const useWhatsAppClocks = (
  state: WhatsAppConversationState,
  readAt: number,
): WhatsAppClocks => {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);
  const offset = useMemo(
    () => serverOffsetMs(state.server_now, readAt),
    [state.server_now, readAt],
  );
  return {
    freeText: remainingMs(state.deadline_24h, nowMs, offset),
    freeMessages: remainingMs(state.deadline_ctwa, nowMs, offset),
  };
};

const Bar = ({
  testId,
  title,
  text,
  tone,
  fraction,
}: {
  testId: string;
  title: string;
  text: string;
  tone: ClockTone;
  fraction: number;
}) => (
  <div data-testid={testId} data-tone={tone} className="flex flex-col gap-1">
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="font-semibold text-foreground">{title}</span>
      <span className="text-muted-foreground">{text}</span>
    </div>
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full transition-[width]"
        style={{
          width: `${fraction * 100}%`,
          backgroundColor: TONE_COLORS[tone],
        }}
      />
    </div>
  </div>
);

/**
 * Almikia Epic EW2 (TASK-079): the two clock bars of the conversation. "Escribir libre" (24 h:
 * green above 6 h, yellow below 6 h, red below 1 h, "Solo plantillas" at zero) and, ONLY on
 * WhatsApp-button (ctwa) leads, "Mensajes gratis" (7 days, "Los mensajes ya tienen costo" at
 * zero). The colour is never the only signal: every bar carries its text.
 */
export const WhatsAppClockBars = ({ clocks }: { clocks: WhatsAppClocks }) => {
  const translate = useTranslate();
  const { freeText, freeMessages } = clocks;
  const freeTextBarTone = freeTextTone(freeText ?? 0);
  const freeMessagesBarTone = freeMessagesTone(freeMessages ?? 0);

  return (
    <div className="flex flex-col gap-3" data-testid="whatsapp-clock-bars">
      <Bar
        testId="whatsapp-clock-free-text"
        title={translate("crm.whatsapp.conversation.clock.free_text_title")}
        tone={freeTextBarTone}
        fraction={barFraction(freeText ?? 0, FREE_TEXT_WINDOW_MS)}
        text={
          freeTextBarTone === "closed"
            ? translate("crm.whatsapp.conversation.clock.free_text_closed")
            : translate("crm.whatsapp.conversation.clock.free_text_left", {
                time: formatRemaining(freeText ?? 0),
              })
        }
      />
      {freeMessages != null ? (
        <Bar
          testId="whatsapp-clock-free-messages"
          title={translate(
            "crm.whatsapp.conversation.clock.free_messages_title",
          )}
          tone={freeMessagesBarTone}
          fraction={barFraction(freeMessages, FREE_MESSAGES_WINDOW_MS)}
          text={
            freeMessagesBarTone === "closed"
              ? translate(
                  "crm.whatsapp.conversation.clock.free_messages_closed",
                )
              : translate(
                  "crm.whatsapp.conversation.clock.free_messages_left",
                  { time: formatRemaining(freeMessages) },
                )
          }
        />
      ) : null}
    </div>
  );
};
